import type {
  BillingCatalogDTO, BillingPlanDTO, BillingStatusDTO, CheckoutSessionDTO, PlayBillingStatusDTO, PortalSessionDTO, PurchasablePlanCode, VerifyPlayPurchase,
} from "@bridewell/shared";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { http } from "./client";
import { keys } from "./keys";

/*
 * The account's plan, whichever store sold it: Paddle on the web (checkout on its own origin, self-service actions)
 * and Google Play in the Android app. Only the server grants a tier; a client never decides what a purchase is worth.
 */

export const billingStatusQuery = queryOptions({ queryKey: keys.billing, queryFn: async () => (await http.get<BillingStatusDTO>("/me/billing")).data, staleTime: 15_000 });
export const billingPlansQuery = queryOptions({ queryKey: keys.billingPlans, queryFn: async () => (await http.get<BillingPlanDTO[]>("/me/billing/plans")).data, staleTime: 10 * 60_000 });

/** Signed-out pricing page. `enabled: false` is a normal answer; a 503 (provider down) is not worth retrying. */
export const publicPlansQuery = queryOptions({
  queryKey: keys.publicPlans,
  queryFn: async () => (await http.get<BillingCatalogDTO>("/billing/plans")).data,
  staleTime: 10 * 60_000,
  retry: false,
});

/** After any plan change: plan status, /me, usage, planner, and every wedding (caps + headroom depend on the owner's plan). */
export function useInvalidateBilling() {
  const qc = useQueryClient();
  return async () => {
    await qc.invalidateQueries({ queryKey: keys.billing });
    await qc.invalidateQueries({ queryKey: keys.me });
    await qc.invalidateQueries({ queryKey: keys.usage });
    await qc.invalidateQueries({ queryKey: keys.planner });
    await qc.invalidateQueries({ queryKey: ["wedding"] });
  };
}

/* ───────── Paddle (web) ───────── */

export function useStartCheckout() {
  return useMutation({
    mutationFn: async (plan: PurchasablePlanCode) => (await http.post<CheckoutSessionDTO>("/me/billing/checkout", { plan, consent: true })).data,
  });
}

export function useBillingAction(action: "cancel" | "resume" | "withdraw") {
  const invalidate = useInvalidateBilling();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await http.post<BillingStatusDTO>(`/me/billing/${action}`)).data,
    onSuccess: async (status) => { qc.setQueryData(keys.billing, status); await invalidate(); },
  });
}

/** Switch tier/interval on the running subscription (prorated by the provider). */
export function useChangePlan() {
  const invalidate = useInvalidateBilling();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (plan: PurchasablePlanCode) => (await http.post<BillingStatusDTO>("/me/billing/plan", { plan })).data,
    onSuccess: async (status) => { qc.setQueryData(keys.billing, status); await invalidate(); },
  });
}

/** Set the number of Pro capacity packs. */
export function useSetPacks() {
  const invalidate = useInvalidateBilling();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (quantity: number) => (await http.post<BillingStatusDTO>("/me/billing/packs", { quantity })).data,
    onSuccess: async (status) => { qc.setQueryData(keys.billing, status); await invalidate(); },
  });
}

export function useBillingPortal() {
  return useMutation({ mutationFn: async () => (await http.post<PortalSessionDTO>("/me/billing/portal")).data });
}

/* ───────── Google Play (Android) ───────── */

/** Whether this account can buy through Google Play right now (false until the server holds Google credentials). */
export const playBillingQuery = queryOptions({
  queryKey: keys.playBilling,
  queryFn: async () => (await http.get<PlayBillingStatusDTO>("/me/billing/google")).data,
  staleTime: 60_000,
  retry: false,
});

/**
 * Hands a Google Play purchase to the server, which asks Google about the token. Only `granted: true` lets the app
 * finish the purchase; `pending` means Google has not settled the payment yet (nothing granted).
 */
export function useVerifyPlayPurchase() {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: async (input: VerifyPlayPurchase) => {
      const res = await http.post<BillingStatusDTO>("/me/billing/google/verify", input);
      return { status: res.data, pending: res.meta?.pending === true, granted: res.meta?.granted === true };
    },
    onSuccess: invalidate,
  });
}
